import * as React from 'react';
import { useCallback } from 'react';

/** Material UI */
import { Typography, MenuItem, Select, Tooltip } from '@mui/material';
import { GridActionsCellItem, useGridApiContext } from '@mui/x-data-grid-premium';

/** Material UI Icons */
import DeleteIcon from '@mui/icons-material/Delete';
import UploadIcon from '@mui/icons-material/Upload';

/** Internal Components */
import { customized_style, dutyEntryDetailCellTypographySx } from 'shared/config/Styles';
import { createQuickFilterFn } from 'shared/lib/quickFilterUtils';
import { parseDateToLocal } from 'shared/lib/dateFormatter';
import {
    createTradePartnerValueParser,
    parsePastedDate,
    tradePartnerValueFormatter,
} from 'shared/lib/datagridHelpers';
import { CustomAutocompleteCell_TradePartner, DateTypingEditCell } from 'shared/ui/RenderCell';
import { GridFilterInputValue } from '@mui/x-data-grid-premium';
import { SOJU_OVER_LIMIT_COLOR, formatProofVolume } from '../../lib/sojuTier';

/** Default values for a newly inserted duty entry row (shared). */
export const DEFAULTS = {
    il_trade_partner: null,
    import_date: null,
    import_date_quarter: null,
    opus_reference: '',
    entry_number: '',
    contract_number: '',
    entry_invoice_date: null,
    entry_fee: null,
    trucking_invoice_date: null,
    trucking_fee: null,
    invoice_number: '',
};

/**
 * The CBMA refund bands an operator can file a detail row under.
 *
 * Soju gets the large refund (Tier 1) only for the first 100,000 proof gallons a producer ships
 * in a year and the smaller one (Tier 2) past that; beer has its own band. Picking Tier 1 is what
 * the pre-save limit check watches for — sojuTier.js keeps its own copy of that code, and
 * SojuTierCodes.test.js fails if the two copies drift apart.
 */
export const TIER1_CODE = 'S01010';
export const TIER2_CODE = 'S01020';
export const BEER_CODE = 'B01010';

/** What an operator can pick in the TIER cell, in dropdown order. '' is "not picked yet". */
export const TIER_CODES = ['', TIER1_CODE, TIER2_CODE, BEER_CODE];

/**
 * Tier를 골라야 하는 품목인데 아직 고르지 않은 상세 행인가.
 *
 * 서버는 행마다 사실 값(display_hints.tier_required·tier_missing)만 내려 주고, 그 행을 어떻게
 * 보일지와 Update ERP를 막을지는 화면이 이 판정으로 정한다. 직접 입력(M.C) 행은 서버가
 * 이미 제외해 보낸다.
 */
export const isTierSelectionMissing = (row) =>
    Boolean(row?.display_hints?.tier_required) && Boolean(row?.display_hints?.tier_missing);

/** Update ERP를 막아야 하는 상세 행들 — 담당자에게 어느 품목의 Tier를 골라야 하는지 알려 주는 재료. */
export const collectTierMissingRows = (details) => (details ?? []).filter(isTierSelectionMissing);

/** Tier 미선택 행에서 담당자가 채워야 할 칸(Tier 셀과 Tier에 따라 정해지는 세목 칸)을 붉게 표시한다. */
const emptyCellClassName = (field) => (params) => {
    const row = params.row;
    if (!isTierSelectionMissing(row)) return '';
    const flagged = field === 'tier' || (row?.display_hints?.tier_duties ?? []).includes(field);
    return flagged ? 'duty-detail-cell--empty' : '';
};

// Quantity, goods value, fees and duties cannot be negative — the screen catches typing and paste mistakes.
export const isNegativeAmount = (value) => Number(value) < 0;
export const negativeValueMessage = (label) => `${label} cannot be negative.`;

// Entering a negative value puts the cell in an error state and blocks the save (shared by numeric columns)
// When row editing starts from a keystroke, MUI calls this guard without props even on
// non-editable cells (the duty columns of a row with M.C off), so undefined passes through
// unchecked to keep the screen from crashing.
export const rejectNegativeEditProps = (params) => ({
    ...params.props,
    error: isNegativeAmount(params.props?.value),
});

/** Local select editor for Tier without stealing focus from other columns. */
const TierEditCell = ({ params, options = [] }) => {
    const apiRef = useGridApiContext();

    const handleChange = React.useCallback(
        (event) => {
            const nextValue = event.target.value ?? '';
            apiRef.current?.setEditCellValue({
                id: params.id,
                field: params.field,
                value: nextValue,
            });
            apiRef.current?.stopCellEditMode({ id: params.id, field: params.field });
        },
        [apiRef, params.field, params.id],
    );

    return (
        <Select
            value={params.value ?? ''}
            onChange={handleChange}
            autoFocus={false}
            sx={{
                width: '100%',
                border: 'none',
                height: '100%',
                '& .MuiSelect-select': {
                    fontSize: '11px',
                    p: '3px',
                },
            }}
        >
            {options.map((item) => (
                <MenuItem key={item} value={item} sx={{ fontSize: '11px', px: 1 }}>
                    {item}
                </MenuItem>
            ))}
        </Select>
    );
};

/**
 * Shared definition for an entry's date columns — Import Date, Entry INV Date and
 * Trucking INV Date all follow the same rules.
 *
 * The main table and the bulk create dialog share this one definition, so an operator never
 * enters or reads the same date differently between the two screens (identical display format,
 * paste handling and stored value). What differs per screen (width, editable marker, the notice
 * shown when a paste is rejected) is layered on through `overrides`.
 */
export const createDateColumn = ({ field, headerName, ...overrides }) => ({
    field,
    headerName,
    align: 'center',
    headerAlign: 'center',
    flex: 0.4,
    editable: true,
    type: 'date',
    pastedValueParser: parsePastedDate,
    headerClassName: 'custom-editable-header',
    valueGetter: (params) => {
        return parseDateToLocal(params);
    },
    valueFormatter: (params) => {
        if (params) {
            const date = parseDateToLocal(params);
            if (date) {
                return date.toLocaleDateString('en-US');
            }
        }
        return params;
    },
    renderCell: (params) => {
        if (params.value != null) {
            const date = parseDateToLocal(params.value);
            if (date) {
                const formattedDate = date.toLocaleDateString('en-US', {
                    year: '2-digit',
                    month: '2-digit',
                    day: '2-digit',
                });
                return <Typography sx={customized_style.itemDatagrid_Typography}>{formattedDate}</Typography>;
            }
        }
        return null;
    },
    renderEditCell: (params) => <DateTypingEditCell params={params} />,
    ...overrides,
});

/** Columns for the main grid displaying duty records. */
export const getColumns = ({ handleDeleteClick, handleUpdateErpClick, clientList }) => [
    {
        field: 'id',
        headerName: 'ID',
        align: 'center',
        headerAlign: 'center',
    },
    {
        field: 'il_trade_partner',
        headerName: 'Client',
        align: 'center',
        headerAlign: 'center',
        flex: 0.4,
        editable: true,
        headerClassName: 'custom-editable-header',
        valueGetter: (params) => {
            return params;
        },
        valueFormatter: tradePartnerValueFormatter,
        valueParser: createTradePartnerValueParser('il_trade_partner', clientList),
        groupingValueGetter: (value) => {
            return value?.name || '';
        },
        renderCell: (params) => {
            return (
                <Typography sx={customized_style.itemDatagrid_Typography}>
                    {params.value?.name || params.value}
                </Typography>
            );
        },
        renderEditCell: (params) => {
            return <CustomAutocompleteCell_TradePartner params={params} options={clientList} />;
        },
        filterOperators: [
            {
                label: 'contains',
                value: 'contains',
                InputComponent: GridFilterInputValue,
                getApplyFilterFn: (filterItem) => {
                    if (!filterItem.value) {
                        return null;
                    }
                    return (value) => {
                        const lowerCaseValue = value.name?.toString().toLowerCase();
                        const lowerCaseFilterValue = filterItem.value.toLowerCase();
                        return lowerCaseValue?.includes(lowerCaseFilterValue);
                    };
                },
            },
        ],
        getApplyQuickFilterFn: createQuickFilterFn,
    },
    createDateColumn({ field: 'import_date', headerName: 'Import Date' }),
    {
        field: 'import_date_quarter',
        headerName: 'Quarter',
        align: 'center',
        headerAlign: 'center',
        flex: 0.4,
    },
    {
        field: 'opus_reference',
        headerName: 'Opus Ref',
        align: 'center',
        headerAlign: 'center',
        flex: 0.4,
        editable: true,
        headerClassName: 'custom-editable-header',
    },
    {
        field: 'entry_number',
        headerName: 'Entry No.',
        align: 'center',
        headerAlign: 'center',
        flex: 0.4,
        editable: true,
        headerClassName: 'custom-editable-header',
    },
    {
        field: 'contract_number',
        headerName: 'Contract No.',
        align: 'center',
        headerAlign: 'center',
        flex: 0.4,
        editable: true,
        headerClassName: 'custom-editable-header',
    },
    createDateColumn({ field: 'entry_invoice_date', headerName: 'Entry INV Date' }),
    {
        field: 'entry_fee',
        headerName: 'Entry Fee',
        align: 'center',
        headerAlign: 'center',
        flex: 0.4,
        editable: true,
        preProcessEditCellProps: rejectNegativeEditProps,
        // 'decimal' is not a MUI column type, so the cell behaved as a string. Committing
        // as a number lets unchanged values skip the save and a cleared cell become "no value".
        type: 'number',
        headerClassName: 'custom-editable-header',
        valueFormatter: (params) => {
            const value = params ?? 0;
            return `$${Number(value).toFixed(2)}`;
        },
    },
    createDateColumn({ field: 'trucking_invoice_date', headerName: 'Trucking INV Date' }),
    {
        field: 'trucking_fee',
        headerName: 'Trucking Fee',
        align: 'center',
        headerAlign: 'center',
        flex: 0.4,
        editable: true,
        preProcessEditCellProps: rejectNegativeEditProps,
        type: 'number', // 'number' instead of 'decimal' for the same reason as entry_fee
        headerClassName: 'custom-editable-header',
        valueFormatter: (params) => {
            const value = params ?? 0;
            return `$${Number(value).toFixed(2)}`;
        },
    },
    {
        field: 'reference',
        headerName: 'Reference',
        align: 'center',
        headerAlign: 'center',
        flex: 0.4,
        editable: true,
        headerClassName: 'custom-editable-header',
    },
    // How much soju this entry shipped, in proof gallons — whatever tier the operator filed it under.
    {
        field: 'soju_pfg',
        headerName: 'Soju PFG',
        type: 'number',
        align: 'center',
        headerAlign: 'center',
        flex: 0.4,
        editable: false,
        renderCell: (params) => (
            <Typography sx={customized_style.itemDatagrid_Typography}>{formatProofVolume(params.value)}</Typography>
        ),
    },
    // Where the producer's year stands after this entry. Red means this is the entry that crossed
    // the 100,000 PFG Tier 1 limit — from here on the volume has to be filed as Tier 2.
    {
        field: 'soju_cumulative_pfg',
        headerName: 'YTD Soju PFG',
        type: 'number',
        align: 'center',
        headerAlign: 'center',
        flex: 0.5,
        editable: false,
        renderCell: (params) => (
            <Typography
                sx={{
                    ...customized_style.itemDatagrid_Typography,
                    ...(params.row?.soju_over_limit === true && { color: SOJU_OVER_LIMIT_COLOR }),
                }}
            >
                {formatProofVolume(params.value)}
            </Typography>
        ),
    },
    {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        align: 'center',
        headerAlign: 'center',
        flex: 0.35,
        getActions: (params) => {
            return [
                <Tooltip title="Update ERP" arrow placement="top">
                    <GridActionsCellItem
                        sx={{ bgcolor: '#003a52', color: 'white', '&:hover': { color: '#003a52' }, p: '5px' }}
                        icon={<UploadIcon sx={{ fontSize: '13px' }} />}
                        label="update erp"
                        onClick={() => handleUpdateErpClick?.(params.row)}
                        color="secondary"
                        key="update-erp"
                    />
                </Tooltip>,
                <Tooltip title="Delete" arrow placement="top">
                    <GridActionsCellItem
                        sx={{ bgcolor: '#003a52', color: 'white', '&:hover': { color: '#003a52' }, p: '5px' }}
                        icon={<DeleteIcon sx={{ fontSize: '13px' }} />}
                        label="delete"
                        onClick={() => handleDeleteClick(params.id)}
                        color="secondary"
                        key="delete"
                    />
                </Tooltip>,
            ];
        },
    },
];

/** Columns for the detail panel (child grid) that displays duty line items. */
export const getDetailPanelColumns = ({ handleDeleteEntryDetail, itemFormulasRows }) => {
    /** -------------------------
     * 1. Fixed columns
     * ------------------------- */
    const baseColumns = [
        {
            field: 'id',
            headerName: 'ID',
            align: 'center',
            headerAlign: 'center',
            flex: 0.1,
            renderCell: (params) => <Typography sx={dutyEntryDetailCellTypographySx}>{params.value}</Typography>,
        },
        {
            field: 'item_code',
            headerName: 'Item Code',
            align: 'center',
            headerAlign: 'center',
            flex: 0.4,
            editable: true,
            renderCell: (params) => (
                <Typography
                    sx={{
                        ...dutyEntryDetailCellTypographySx,
                        ...(params.value === 'UNKNOWN' && { color: 'error.main' }),
                    }}
                >
                    {params.value}
                </Typography>
            ),
        },
        {
            field: 'item_name',
            headerName: 'Item Name',
            align: 'center',
            headerAlign: 'center',
            flex: 0.7,
            editable: true,
            renderCell: (params) => (
                <Typography
                    sx={{
                        ...customized_style.itemDatagrid_Typography,
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        fontSize: '11px',
                    }}
                >
                    {params.value}
                </Typography>
            ),
        },
        {
            field: 'description',
            headerName: 'Description',
            align: 'center',
            headerAlign: 'center',
            flex: 0.7,
            editable: true,
            renderCell: (params) => (
                <Typography
                    sx={{
                        ...customized_style.itemDatagrid_Typography,
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        fontSize: '11px',
                    }}
                >
                    {params.value}
                </Typography>
            ),
        },
        {
            field: 'quantity',
            headerName: 'Quantity',
            type: 'number',
            align: 'center',
            headerAlign: 'center',
            flex: 0.3,
            editable: true,
            preProcessEditCellProps: rejectNegativeEditProps,
            // renderCell: (params) => <Typography sx={dutyEntryDetailCellTypographySx}>{params.value}</Typography>,
            renderCell: (params) => (
                <Typography sx={dutyEntryDetailCellTypographySx}>
                    {params.value != null && !Number.isNaN(Number(params.value))
                        ? Number(params.value).toLocaleString()
                        : params.value}
                </Typography>
            ),
        },
        {
            field: 'unit_price',
            headerName: 'Unit Price',
            type: 'number',
            align: 'center',
            headerAlign: 'center',
            flex: 0.3,
            editable: true,
            aggregable: false,
            renderCell: (params) => {
                if (params.rowNode?.id === 'auto-generated-group-footer-root') {
                    return null;
                }
                const value = Number(params.value ?? 0);
                return <Typography sx={dutyEntryDetailCellTypographySx}>{`$${value.toFixed(2)}`}</Typography>;
            },
        },
        {
            field: 'invoice_amount',
            headerName: 'Invoice Amount',
            type: 'number',
            align: 'center',
            headerAlign: 'center',
            flex: 0.4,
            editable: true,
            preProcessEditCellProps: rejectNegativeEditProps,
            renderCell: (params) => {
                const value = Number(params.value ?? 0);
                return <Typography sx={dutyEntryDetailCellTypographySx}>{`$${value.toFixed(2)}`}</Typography>;
            },
        },
        {
            field: 'tier',
            headerName: 'TIER',
            type: 'singleSelect',
            align: 'center',
            headerAlign: 'center',
            flex: 0.2,
            editable: true,
            cellClassName: emptyCellClassName('tier'),
            valueSetter: (value, row) => ({ ...row, tier: value ?? '' }),
            renderCell: (params) => <Typography sx={dutyEntryDetailCellTypographySx}>{params.value}</Typography>,
            renderEditCell: (params) => <TierEditCell params={params} options={TIER_CODES} />,
        },
    ];

    /** -------------------------
     * 2. Dynamic columns derived from duty_formula_name
     * ------------------------- */
    const dynamicDutyColumns = [...new Set(itemFormulasRows.map((row) => row.duty_name))].map((dutyName) => ({
        field: dutyName,
        headerName: dutyName,
        type: 'number',
        align: 'center',
        headerAlign: 'center',
        flex: 0.3,
        editable: true,
        cellClassName: emptyCellClassName(dutyName),
        preProcessEditCellProps: rejectNegativeEditProps,
        renderCell: (params) => {
            const value = Number(params.value ?? 0);
            return <Typography sx={dutyEntryDetailCellTypographySx}>{`$${value.toFixed(2)}`}</Typography>;
        },
    }));

    const checkManualCalculated = {
        field: 'manual_calculated',
        headerName: 'M.C',
        type: 'boolean',
        align: 'center',
        headerAlign: 'center',
        flex: 0.1,
        editable: true,
    };

    /** -------------------------
     * 3. Action column
     * ------------------------- */
    const actionColumn = {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        align: 'center',
        headerAlign: 'center',
        flex: 0.2,
        cellClassName: 'actions',
        aggregable: false,
        renderCell: (params) => {
            if (params.rowNode?.id === 'auto-generated-group-footer-root') return null;
            return (
                <Tooltip title="Delete" arrow placement="top">
                    <GridActionsCellItem
                        icon={<DeleteIcon sx={{ fontSize: '14px' }} />}
                        label="delete"
                        onClick={() => handleDeleteEntryDetail(params.id)}
                    />
                </Tooltip>
            );
        },
    };

    return [...baseColumns, ...dynamicDutyColumns, checkManualCalculated, actionColumn];
};

export const getUploadItemColumns = () => {
    return [
        {
            field: 'id',
            headerName: 'ID',
            align: 'center',
            headerAlign: 'center',
            flex: 0.1,
            renderCell: (params) => <Typography sx={dutyEntryDetailCellTypographySx}>{params.value}</Typography>,
        },
        {
            field: 'item_code',
            headerName: 'Item Code',
            align: 'center',
            headerAlign: 'center',
            flex: 0.4,
            editable: true,
            renderCell: (params) => <Typography sx={dutyEntryDetailCellTypographySx}>{params.value}</Typography>,
        },
        {
            field: 'item_name',
            headerName: 'Item Name',
            align: 'center',
            headerAlign: 'center',
            flex: 0.7,
            editable: true,
            renderCell: (params) => <Typography sx={dutyEntryDetailCellTypographySx}>{params.value}</Typography>,
        },
        {
            field: 'quantity',
            headerName: 'Quantity',
            type: 'number',
            align: 'center',
            headerAlign: 'center',
            flex: 0.3,
            editable: true,
            preProcessEditCellProps: rejectNegativeEditProps,
            renderCell: (params) => <Typography sx={dutyEntryDetailCellTypographySx}>{params.value}</Typography>,
        },
        {
            field: 'invoice_amount',
            headerName: 'Invoice Amount',
            type: 'number',
            align: 'center',
            headerAlign: 'center',
            flex: 0.4,
            editable: true,
            preProcessEditCellProps: rejectNegativeEditProps,
            renderCell: (params) => (
                <Typography sx={dutyEntryDetailCellTypographySx}>{`$${Number(params.value).toFixed(2)}`}</Typography>
            ),
        },
        {
            field: 'tier',
            headerName: 'TIER',
            type: 'singleSelect',
            align: 'center',
            headerAlign: 'center',
            flex: 0.3,
            editable: true,
            renderCell: (params) => <Typography sx={dutyEntryDetailCellTypographySx}>{params.value}</Typography>,
            renderEditCell: (params) => <TierEditCell params={params} options={TIER_CODES} />,
        },
    ];
};
